import React from 'react';
import PropTypes from 'prop-types';

/**
 * TabBar - Einheitliche Tab-Komponente
 * 
 * Varianten:
 * - underline: Tabs mit Unterstrich (Standard)
 * - pills: Tabs als Pills
 * - buttons: Tabs als Buttons
 * 
 * Usage:
 * <TabBar
 *   tabs={[
 *     { id: 'courses', label: 'Kurse', icon: '📚', count: 5 },
 *     { id: 'templates', label: 'Vorlagen', icon: '📝', count: 12 }
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   variant="underline"
 * />
 */
export default function TabBar({
  tabs,
  activeTab,
  onTabChange,
  variant = 'underline',
  size = 'md',
  className = ''
}) {
  const baseClasses = {
    underline: 'flex items-center gap-1',
    pills: 'flex items-center gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg',
    buttons: 'flex items-center gap-2'
  };

  const tabBaseClasses = {
    underline: 'relative px-3 py-2 text-sm font-medium theme-transition',
    pills: 'px-4 py-2 text-sm font-medium rounded-md theme-transition',
    buttons: 'px-4 py-2 text-sm font-medium rounded-lg border theme-transition'
  };

  const tabActiveClasses = {
    underline: 'text-primary-600 dark:text-primary-400',
    pills: 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm',
    buttons: 'bg-primary-600 text-white border-primary-600'
  };

  const tabInactiveClasses = {
    underline: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
    pills: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
    buttons: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
  };

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  return (
    <div className={`${baseClasses[variant]} ${className}`}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
              ${tabBaseClasses[variant]}
              ${sizeClasses[size]}
              ${isActive ? tabActiveClasses[variant] : tabInactiveClasses[variant]}
              flex items-center gap-2
            `}
          >
            {/* Icon */}
            {tab.icon && <span>{tab.icon}</span>}
            
            {/* Label */}
            <span>{tab.label}</span>
            
            {/* Count Badge */}
            {tab.count !== undefined && (
              <span className={`
                ml-1 px-2 py-0.5 text-xs rounded-full
                ${isActive 
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}>
                {tab.count}
              </span>
            )}
            
            {/* Underline indicator */}
            {variant === 'underline' && isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400" />
            )}
          </button>
        );
      })}
    </div>
  );
}

TabBar.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    count: PropTypes.number
  })).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['underline', 'pills', 'buttons']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};
